import { QueryKeys, API_ENDPOINT } from "@cede/utils";
import { TradeStatItem } from "@/components/features/trade/TradeStats/types";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useCampaignClaimRewards } from "@/hooks/campaign/useCampaignClaimRewards";
import { useApi } from "@/hooks/core/useApi";
import { useTmaOnboardingRewards } from "@/hooks/quests/useTmaOnboardingRewards";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

interface UseUserStatsOptions {
  /** Whether to enable the query (default: true) */
  enabled?: boolean;
}

export const useUserStats = (options?: UseUserStatsOptions) => {
  const queryEnabled = options?.enabled ?? true;
  const { hubUser, auth } = useHubUser();
  const apiHelper = useApi();
  const { isTMA } = useTelegramContext();

  // Only fetch TMA onboarding rewards when in TMA environment and query is enabled
  const tmaRewards = useTmaOnboardingRewards({ enabled: queryEnabled });
  const totalUsdtRewardsClaimable = isTMA ? tmaRewards.totalUsdtRewardsClaimable : 0;
  const totalUsdtRewardsReceived = isTMA ? tmaRewards.totalUsdtRewardsReceived : 0;

  const {
    totalRewardAmountFiat,
    totalSoonRewardAmountFiat,
    isLoading: isLoadingClaimRewards,
    isLoadingClaimableSoonEpochs,
  } = useCampaignClaimRewards({ enabled: queryEnabled });

  const { data: stats, isLoading } = useQuery({
    queryKey: [QueryKeys.USER_STATS, hubUser?.defiAddress],
    queryFn: () => apiHelper.getWithAuth(auth, API_ENDPOINT.USER_STATS, {}),
    enabled: queryEnabled && !!hubUser?.defiAddress && !!auth?.identityToken,
    // `useCampaigns` also mounts this query, so polling here hits USER_STATS across the campaign list.
    // Refresh on mount/focus, after claims, and through the post-index user SSE event.
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000,
  });

  if (!stats) return { stats: null, participatingCampaigns: [], isLoading };

  const profileStatsData: Record<string, TradeStatItem> = {
    totalVolumeTraded: {
      title: "Total Volume Traded",
      value: stats.totalVolumeTraded.toNumber(),
      isMoney: true,
    },
    totalVolumeRewarded: {
      title: "Total Earned",
      value: stats.totalRewardsReceived.add(totalUsdtRewardsReceived).toNumber(),
      isMoney: true,
    },
    tradesDone: {
      title: "Trades Done",
      value: stats.totalNumberOfTrades,
      isMoney: false,
      isInteger: true,
    },
    claimableNow: {
      title: "Claimable Now",
      value: totalRewardAmountFiat + totalUsdtRewardsClaimable,
      isMoney: true,
    },
    claimableSoon: {
      title: "Claimable Soon",
      value: totalSoonRewardAmountFiat,
      tooltipText:
        "Rewards are locked for 24 hours after the end of the epoch. You can claim them anytime after the locking period.",
      isMoney: true,
    },
  };

  return {
    stats: profileStatsData,
    participatingCampaigns: stats.campaignsParticipatingList || [],
    isLoading: isLoading || isLoadingClaimRewards || isLoadingClaimableSoonEpochs,
  };
};
